import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Minus, Check, Camera, Scale, Flame, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import StatTile from "../components/StatTile";

export default function Dashboard() {
  const [dash, setDash] = useState(null);
  const [habit, setHabit] = useState({ sugary_drinks: 0, treadmill_completed: false });
  const [weightInput, setWeightInput] = useState("");
  const [savingWeight, setSavingWeight] = useState(false);
  const [targetEditing, setTargetEditing] = useState(false);
  const [targetInput, setTargetInput] = useState("");

  const today = new Date().toISOString().slice(0, 10);

  const load = async () => {
    try {
      const [d, h] = await Promise.all([api.dashboard(), api.todayHabit()]);
      setDash(d);
      setHabit({ sugary_drinks: h.sugary_drinks || 0, treadmill_completed: !!h.treadmill_completed });
    } catch (e) {
      toast.error("Couldn't load dashboard");
    }
  };

  useEffect(() => { load(); }, []);

  const updateHabit = async (patch) => {
    const next = { ...habit, ...patch };
    setHabit(next);
    try {
      await api.upsertHabit({ date: today, ...patch });
      load();
    } catch {
      toast.error("Couldn't save habit");
    }
  };

  const saveWeight = async () => {
    const w = parseFloat(weightInput);
    if (!w || w <= 0 || w > 500) {
      toast.error("Enter a valid weight in kg");
      return;
    }
    setSavingWeight(true);
    try {
      await api.addWeight({ date: today, weight: w });
      setWeightInput("");
      toast.success("Weight logged");
      load();
    } catch {
      toast.error("Couldn't save weight");
    } finally {
      setSavingWeight(false);
    }
  };

  const saveTarget = async () => {
    const v = parseInt(targetInput, 10);
    if (!v || v <= 0 || v > 20000) {
      toast.error("Enter a daily calorie target");
      return;
    }
    try {
      await api.saveSettings({ daily_calorie_target: v });
      setTargetEditing(false);
      toast.success("Daily budget saved");
      load();
    } catch {
      toast.error("Couldn't save budget");
    }
  };

  const greet = () => {
    const hr = new Date().getHours();
    if (hr < 12) return "Good morning";
    if (hr < 18) return "Good afternoon";
    return "Good evening";
  };

  const target = dash?.daily_calorie_target || null;
  const tMin = dash?.today_calories_min || 0;
  const tMax = dash?.today_calories_max || 0;
  const tMid = Math.round((tMin + tMax) / 2);
  const pct = target ? Math.min(100, Math.round((tMid / target) * 100)) : 0;
  const over = target ? tMid > target : false;

  return (
    <div className="px-5 pt-8 pb-6 space-y-6" data-testid="dashboard-page">
      <header>
        <p className="text-xs uppercase tracking-[0.2em] font-bold text-sage">{today}</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-charcoal mt-2">
          {greet()}.
        </h1>
        <p className="text-slate2 mt-2 text-sm leading-relaxed">
          Small, consistent choices. No streaks. No guilt. Just awareness.
        </p>
      </header>

      {/* Quick actions */}
      <div className="grid grid-cols-2 gap-3">
        <Link
          to="/log"
          data-testid="quick-log-meal"
          className="card-soft p-5 tap-shrink hover:shadow-floating transition-shadow flex flex-col gap-2"
        >
          <div className="w-10 h-10 rounded-2xl bg-sage-light flex items-center justify-center">
            <Camera size={20} strokeWidth={1.75} className="text-sage" />
          </div>
          <p className="font-heading font-bold text-charcoal">Log a meal</p>
          <p className="text-xs text-slate2">Snap & analyze</p>
        </Link>
        <div className="card-soft p-5 flex flex-col gap-2">
          <div className="w-10 h-10 rounded-2xl bg-ochre-light flex items-center justify-center">
            <Scale size={20} strokeWidth={1.75} className="text-ochre" />
          </div>
          <p className="font-heading font-bold text-charcoal">Weight (kg)</p>
          <div className="flex items-center gap-2">
            <input
              type="number"
              step="0.1"
              inputMode="decimal"
              placeholder={dash?.current_weight ? String(dash.current_weight) : "—"}
              value={weightInput}
              onChange={(e) => setWeightInput(e.target.value)}
              data-testid="weight-input"
              className="flex-1 min-w-0 rounded-xl bg-sand px-3 py-2 text-sm font-semibold text-charcoal placeholder:text-slate2/60 focus:outline-none focus:ring-2 focus:ring-sage"
            />
            <button
              onClick={saveWeight}
              disabled={savingWeight}
              data-testid="weight-save"
              className="rounded-xl bg-charcoal text-cream px-3 py-2 text-xs font-bold tap-shrink disabled:opacity-50"
            >
              Save
            </button>
          </div>
        </div>
      </div>

      {/* Daily calorie budget (soft) */}
      <section className="card-soft p-5 space-y-3" data-testid="daily-budget">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-lg font-bold text-charcoal flex items-center gap-2">
            <Flame size={18} className="text-ochre" strokeWidth={2} /> Today&apos;s calories
          </h2>
          {target && !targetEditing && (
            <button
              onClick={() => { setTargetInput(String(target)); setTargetEditing(true); }}
              data-testid="edit-budget"
              className="text-slate2 hover:text-sage tap-shrink rounded-full p-1.5"
              aria-label="Edit daily budget"
            >
              <Pencil size={16} strokeWidth={1.75} />
            </button>
          )}
        </div>

        {target && !targetEditing ? (
          <>
            <p className="font-heading text-2xl font-extrabold text-charcoal" data-testid="budget-total">
              {tMin}–{tMax}
              <span className="text-sm font-semibold text-slate2"> of {target} kcal</span>
            </p>
            <div className="h-3 rounded-full bg-sand overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${over ? "bg-ochre" : "bg-sage"}`}
                style={{ width: `${Math.max(pct, 3)}%` }}
              />
            </div>
            <p className="text-xs text-slate2">
              {dash?.today_items_logged
                ? over
                  ? `About ${tMid - target} over your soft budget — no guilt, just awareness.`
                  : `About ${target - tMid} kcal left (best estimate).`
                : "Nothing logged yet today."}
            </p>
          </>
        ) : (
          <div className="space-y-2">
            <p className="text-sm text-slate2">
              {target ? "Update your daily calorie budget." : "Set a gentle daily calorie budget to track awareness — not a hard limit."}
            </p>
            <div className="flex items-center gap-2">
              <input
                type="number"
                inputMode="numeric"
                placeholder="e.g. 2000"
                value={targetInput}
                onChange={(e) => setTargetInput(e.target.value)}
                data-testid="budget-input"
                className="flex-1 min-w-0 rounded-xl bg-sand px-3 py-2 text-sm font-semibold text-charcoal placeholder:text-slate2/60 focus:outline-none focus:ring-2 focus:ring-sage"
              />
              <button
                onClick={saveTarget}
                data-testid="budget-save"
                className="rounded-xl bg-charcoal text-cream px-4 py-2 text-xs font-bold tap-shrink"
              >
                Save
              </button>
              {target && (
                <button
                  onClick={() => setTargetEditing(false)}
                  className="rounded-xl bg-sand text-charcoal px-3 py-2 text-xs font-bold tap-shrink"
                >
                  Cancel
                </button>
              )}
            </div>
          </div>
        )}
      </section>

      {/* Habit bento */}
      <section className="space-y-3">
        <h2 className="font-heading text-lg font-bold text-charcoal">Today&apos;s habits</h2>
        <div className="grid grid-cols-2 gap-3">
          <div
            className={`rounded-3xl p-5 ${habit.sugary_drinks > 0 ? "bg-terracotta-light" : "bg-sand"}`}
            data-testid="habit-sugary"
          >
            <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate2">Sugary drinks</p>
            <p className="font-heading text-4xl font-extrabold text-charcoal mt-2" data-testid="sugary-count">
              {habit.sugary_drinks}
            </p>
            <div className="flex items-center gap-2 mt-3">
              <button
                onClick={() => updateHabit({ sugary_drinks: Math.max(0, habit.sugary_drinks - 1) })}
                data-testid="sugary-decrement"
                className="w-10 h-10 rounded-2xl bg-white text-charcoal flex items-center justify-center tap-shrink shadow-soft"
              >
                <Minus size={18} />
              </button>
              <button
                onClick={() => updateHabit({ sugary_drinks: habit.sugary_drinks + 1 })}
                data-testid="sugary-increment"
                className="flex-1 h-10 rounded-2xl bg-charcoal text-cream flex items-center justify-center tap-shrink font-bold text-sm"
              >
                <Plus size={16} className="mr-1" /> Add
              </button>
            </div>
          </div>

          <button
            onClick={() => updateHabit({ treadmill_completed: !habit.treadmill_completed })}
            data-testid="habit-treadmill"
            className={`rounded-3xl p-5 text-left tap-shrink transition-colors ${
              habit.treadmill_completed ? "bg-sage text-cream" : "bg-sand text-charcoal"
            }`}
          >
            <p className={`text-[11px] uppercase tracking-[0.18em] font-bold ${habit.treadmill_completed ? "text-cream/80" : "text-slate2"}`}>
              Treadmill
            </p>
            <p className="font-heading text-2xl font-extrabold mt-2 leading-tight">
              {habit.treadmill_completed ? "Done today" : "Tap when done"}
            </p>
            <div className={`mt-3 w-10 h-10 rounded-2xl flex items-center justify-center ${
              habit.treadmill_completed ? "bg-cream/20" : "bg-white shadow-soft"
            }`}>
              <Check size={20} className={habit.treadmill_completed ? "text-cream" : "text-slate2"} />
            </div>
          </button>
        </div>
      </section>

      {/* Stats */}
      {dash && (
        <section className="space-y-3" data-testid="dashboard-stats">
          <h2 className="font-heading text-lg font-bold text-charcoal">Snapshot</h2>
          <div className="grid grid-cols-2 gap-3">
            <StatTile
              label="Current weight"
              value={dash.current_weight != null ? `${dash.current_weight} kg` : "—"}
              sub={dash.weight_30d_change != null ? `${dash.weight_30d_change > 0 ? "+" : ""}${dash.weight_30d_change} kg / 30d` : "Log to see trend"}
              accent="sand"
              testid="stat-weight"
            />
            <StatTile
              label="Meals logged"
              value={dash.meals_logged_total}
              sub="All time"
              accent="sage"
              testid="stat-meals"
            />
            <StatTile
              label="Sugary drinks"
              value={dash.sugary_drinks_week}
              sub="This week"
              accent="terracotta"
              testid="stat-sugary"
            />
            <StatTile
              label="Treadmill"
              value={`${dash.treadmill_sessions_week} / 7`}
              sub="This week"
              accent="ochre"
              testid="stat-treadmill"
            />
          </div>
        </section>
      )}

      {/* Common meals */}
      {dash?.common_meals?.length > 0 && (
        <section className="card-soft p-5" data-testid="common-meals">
          <h2 className="font-heading text-lg font-bold text-charcoal">Most common meals</h2>
          <ul className="mt-3 space-y-2">
            {dash.common_meals.map((m, i) => (
              <li key={i} className="flex items-center justify-between text-sm">
                <span className="text-charcoal">{m.name}</span>
                <span className="text-slate2 font-semibold">{m.count}×</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {dash?.common_drinks?.length > 0 && (
        <section className="card-soft p-5" data-testid="common-drinks">
          <h2 className="font-heading text-lg font-bold text-charcoal">Most common drinks</h2>
          <ul className="mt-3 space-y-2">
            {dash.common_drinks.map((m, i) => (
              <li key={i} className="flex items-center justify-between text-sm">
                <span className="text-charcoal">{m.name}</span>
                <span className="text-slate2 font-semibold">{m.count}×</span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
